import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Gift } from "lucide-react";
import api from "@/lib/api";

const DURATIONS = [
  { value: "1y", label: "1 year", helper: "Full access through next year" },
  { value: "2y", label: "2 years", helper: "Full access for 24 months" },
  { value: "5y", label: "5 years", helper: "Full access for half a decade" },
  { value: "lifetime", label: "Lifetime", helper: "Never expires" },
];

export default function GiftAccessDialog({ user, open, onOpenChange, onSuccess }) {
  const [duration, setDuration] = useState("1y");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!user) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/gift-access`, { duration });
      toast.success(
        duration === "lifetime"
          ? `${user.email} now has lifetime access`
          : `${user.email} gifted access for ${DURATIONS.find((d) => d.value === duration)?.label}`
      );
      onSuccess?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to gift access");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="gift-access-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-violet-600" />
            Gift access
          </DialogTitle>
          <DialogDescription>
            Grant <span className="font-medium text-stone-900">{user?.name || user?.email}</span> full Pro
            access without requiring a paid subscription. Existing Stripe payments will be superseded.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2" data-testid="gift-duration-options">
          {DURATIONS.map((d) => {
            const active = duration === d.value;
            return (
              <button
                key={d.value}
                type="button"
                onClick={() => setDuration(d.value)}
                className={`w-full text-left p-3 rounded-xl border transition ${
                  active
                    ? "border-violet-400 bg-violet-50/60 ring-1 ring-violet-200"
                    : "border-stone-200 hover:border-stone-300 bg-white"
                }`}
                data-testid={`gift-duration-${d.value}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-stone-900">{d.label}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{d.helper}</div>
                  </div>
                  <div
                    className={`w-4 h-4 rounded-full border-2 ${
                      active ? "border-violet-600 bg-violet-600" : "border-stone-300"
                    }`}
                  />
                </div>
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid="gift-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="gift-confirm-btn"
          >
            {busy ? "Granting…" : "Grant access"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
